import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import { API_URL } from '../../../../app/modules/auth/core/_requests'

interface ETLData {
  JobDate: string
  JobCategory: string
  JobName: string
  Status: string
  ErrorCode?: string
  ErrorMessage?: string
  AdditionalInfo?: string
}

interface ETLSummary {
  etl: { total: number; success: number; failure: number }
  api: { total: number; success: number; failure: number }
  date: string
  hasFailures: boolean
}

interface FailedJob {
  jobName: string
  category: string
  date: string
  errorCode?: string
  errorMessage?: string
  additionalInfo?: string
}

export const ETLWidget: React.FC = () => {
  const [etlData, setEtlData] = useState<ETLData[]>([])
  const [etlLoading, setEtlLoading] = useState(false)
  const [showEtlWidget, setShowEtlWidget] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  
  // Determine current theme
  const systemMode = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  const currentTheme = document.documentElement.classList.contains('dark') ? 'dark' : 'light'
  
  // Fetch ETL data - always fetch 7 days to get comprehensive data
  const fetchEtlData = async () => {
    setEtlLoading(true)
    try {
      const response = await axios.get(`${API_URL}/logs/etl-dashboard?days=7`)
      setEtlData(response.data)
    } catch (error) {
      console.error('Failed to fetch ETL data:', error)
    } finally {
      setEtlLoading(false)
    }
  }
  
  useEffect(() => {
    fetchEtlData()
    // Refresh every 5 minutes
    const interval = setInterval(fetchEtlData, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  // Close widget when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('.etl-widget-container')) {
        setShowEtlWidget(false)
        setIsHovering(false)
      }
    }

    if (showEtlWidget) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showEtlWidget])

  // Helper function to get status color
  const getStatusColor = (successCount: number, failureCount: number, total: number) => {
    if (total === 0) return '#bdbdbd' // did not run
    if (failureCount === 0) return '#21ba45' // success
    if (failureCount === 1) return '#ffb74d' // 1 failure
    if (failureCount === 2) return '#ff9800' // 2 failures
    if (failureCount >= 3) return '#f57c00' // 3+ failures
    return '#db2828' // failure
  }

  // Helper function to get SVG fill color based on failures
  const getSvgFillColor = () => {
    if (etlLoading) return '#3b82f6' // blue when loading
    if (summary.hasFailures) {
      const totalFailures = summary.etl.failure + summary.api.failure
      if (totalFailures >= 3) return '#ef4444' // red for 3+ failures
      if (totalFailures === 2) return '#ff9800' // orange for 2 failures
      return '#ffb74d' // light orange for 1 failure
    }
    return svgFillColor // default theme color when no failures
  }

  // Get failed jobs from the last 7 days
  const getFailedJobs = (): FailedJob[] => {
    if (etlData.length === 0) return []
    
    const failedJobs: FailedJob[] = []
    
    etlData.forEach(entry => {
      if (entry.Status === 'Failure') {
        failedJobs.push({
          jobName: entry.JobName,
          category: entry.JobCategory,
          date: entry.JobDate,
          errorCode: entry.ErrorCode,
          errorMessage: entry.ErrorMessage,
          additionalInfo: entry.AdditionalInfo
        })
      }
    })
    
    // Sort by date (most recent first) and then by job name
    return failedJobs.sort((a, b) => {
      const dateComparison = new Date(b.date).getTime() - new Date(a.date).getTime()
      if (dateComparison !== 0) return dateComparison
      return a.jobName.localeCompare(b.jobName)
    })
  }

  // Get latest summary (today or last day with data)
  const getLatestSummary = (): ETLSummary => {
    if (etlData.length === 0) {
      return { 
        etl: { total: 0, success: 0, failure: 0 }, 
        api: { total: 0, success: 0, failure: 0 }, 
        date: 'No data',
        hasFailures: false
      }
    }
    
    // Get the most recent date with data
    const dates = Array.from(new Set(etlData.map(d => d.JobDate))).sort().reverse()
    const latestDate = dates[0]
    const latestData = etlData.filter(d => d.JobDate === latestDate)
    
    const etlJobs = latestData.filter(d => d.JobCategory === 'ETL')
    const apiJobs = latestData.filter(d => d.JobCategory === 'API Health Check')
    
    const etlFailures = etlJobs.filter(e => e.Status === 'Failure').length
    const apiFailures = apiJobs.filter(e => e.Status === 'Failure').length
    
    return {
      etl: {
        total: etlJobs.length,
        success: etlJobs.filter(e => e.Status === 'Success').length,
        failure: etlFailures
      },
      api: {
        total: apiJobs.length,
        success: apiJobs.filter(e => e.Status === 'Success').length,
        failure: apiFailures
      },
      date: latestDate,
      hasFailures: etlFailures > 0 || apiFailures > 0
    }
  }

  const summary = getLatestSummary()
  const failedJobs = getFailedJobs()
  const isToday = summary.date === new Date().toISOString().split('T')[0]

  // Theme-aware styles
  const buttonContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    width: '60px',
    height: '50px',
    textAlign: 'center',
    position: 'relative',
    marginTop: '15px'
  }

  const dashboardBtnStyle: React.CSSProperties = {
    background: 'none',
    cursor: 'pointer',
    padding: '5px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    position: 'relative',
    // Add subtle background when there are failures
    backgroundColor: summary.hasFailures ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
    border: summary.hasFailures ? '1px solid rgba(239, 68, 68, 0.2)' : '1px solid transparent'
  }

  const svgFillColor = currentTheme === 'dark' ? '#d1d5db' : '#6b7280'
  const textColor = currentTheme === 'dark' ? '#d1d5db' : '#6b7280'

  // ETL Widget Styles - Modern UI
  const etlWidgetStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: '0',
    width: '360px',
    backgroundColor: currentTheme === 'dark' ? '#111827' : '#ffffff',
    border: `1px solid ${currentTheme === 'dark' ? '#1f2937' : '#f3f4f6'}`,
    borderRadius: '16px',
    boxShadow: currentTheme === 'dark' 
      ? '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)'
      : '0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
    zIndex: 1000,
    padding: '20px',
    opacity: (showEtlWidget || isHovering) ? 1 : 0,
    visibility: (showEtlWidget || isHovering) ? 'visible' : 'hidden',
    transform: (showEtlWidget || isHovering) ? 'translateY(0) scale(1)' : 'translateY(-10px) scale(0.95)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    backdropFilter: 'blur(10px)',
    marginTop: '8px'
  }

  return (
    <div className="etl-widget-container" style={buttonContainerStyle}>
      <button 
        style={dashboardBtnStyle}
        title={summary.hasFailures ? "ETL Status - Issues Detected" : "ETL Status"}
        onClick={() => setShowEtlWidget(!showEtlWidget)}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.1)'
          setIsHovering(true)
          if (summary.hasFailures) {
            e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.15)'
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)'
          setIsHovering(false)
          if (summary.hasFailures) {
            e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)'
          }
        }}
      >
        <i className="pi pi-database" style={{ fontSize: '25px', color: getSvgFillColor() }}></i>
        
        {/* ETL Widget Popup */}
        <div style={etlWidgetStyle}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            paddingBottom: '12px',
            borderBottom: `1px solid ${currentTheme === 'dark' ? '#1f2937' : '#f3f4f6'}`
          }}>
            <div>
              <h4 style={{
                margin: 0,
                fontSize: '16px',
                fontWeight: '700',
                color: currentTheme === 'dark' ? '#f9fafb' : '#111827',
                marginBottom: '4px'
              }}>
                ETL Status
                {summary.hasFailures && (
                  <span style={{
                    marginLeft: '8px',
                    fontSize: '12px',
                    color: '#ef4444',
                    fontWeight: '600'
                  }}>
                    ⚠️ Issues
                  </span>
                )}
              </h4>
              <span style={{
                fontSize: '12px',
                color: currentTheme === 'dark' ? '#9ca3af' : '#6b7280',
                fontWeight: '500'
              }}>
                {summary.date === 'No data' ? 'No recent data' : 
                 isToday ? '' : 
                 `Last run: ${new Date(summary.date).toLocaleDateString()}`}
              </span>
            </div>
                          <Link to='/status-page' style={{
                fontSize: '13px',
                color: '#3b82f6',
                textDecoration: 'none',
                fontWeight: '600',
                padding: '8px 12px',
                borderRadius: '8px',
                backgroundColor: currentTheme === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = currentTheme === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.2)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = currentTheme === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.1)'
              }}>
                View Full
                <i className="pi pi-arrow-right" style={{ fontSize: '12px' }}></i>
              </Link>
          </div>
          
          {etlLoading ? (
            <div style={{
              textAlign: 'center', 
              padding: '32px 20px', 
              color: textColor,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '24px',
                height: '24px',
                border: `2px solid ${currentTheme === 'dark' ? '#374151' : '#e5e7eb'}`,
                borderTop: `2px solid ${currentTheme === 'dark' ? '#3b82f6' : '#3b82f6'}`,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <span style={{fontSize: '14px', fontWeight: '500'}}>Loading status...</span>
            </div>
          ) : (
            <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
              {/* ETL Section */}
              <div style={{
                padding: '16px',
                backgroundColor: currentTheme === 'dark' ? '#1f2937' : '#f9fafb',
                borderRadius: '12px',
                border: `1px solid ${summary.etl.failure > 0 ? 'rgba(239, 68, 68, 0.3)' : currentTheme === 'dark' ? '#374151' : '#e5e7eb'}`,
                boxShadow: summary.etl.failure > 0 ? '0 0 0 1px rgba(239, 68, 68, 0.1)' : 'none'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <i className="pi pi-database" style={{ 
                          fontSize: '16px', 
                          color: currentTheme === 'dark' ? '#9ca3af' : '#6b7280' 
                        }}></i>
                        <span style={{
                          fontSize: '14px', 
                          fontWeight: '600', 
                          color: currentTheme === 'dark' ? '#f9fafb' : '#111827'
                        }}>
                          ETLs
                          {summary.etl.failure > 0 && (
                            <span style={{
                              marginLeft: '6px',
                              fontSize: '11px',
                              color: '#ef4444',
                              fontWeight: '600'
                            }}>
                              ({summary.etl.failure} failed)
                            </span>
                          )}
                        </span>
                    </div>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    backgroundColor: getStatusColor(summary.etl.success, summary.etl.failure, summary.etl.total),
                    border: `2px solid ${currentTheme === 'dark' ? '#374151' : '#ffffff'}`,
                    boxShadow: '0 0 0 2px rgba(0,0,0,0.1)'
                  }}></div>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{display: 'flex', gap: '16px'}}>
                    <div style={{textAlign: 'center'}}>
                      <div style={{
                        fontSize: '20px',
                        fontWeight: '700',
                        color: '#10b981',
                        lineHeight: '1'
                      }}>
                        {summary.etl.success}
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: currentTheme === 'dark' ? '#9ca3af' : '#6b7280',
                        fontWeight: '500'
                      }}>
                        Success
                      </div>
                    </div>
                    <div style={{textAlign: 'center'}}>
                      <div style={{
                        fontSize: '20px',
                        fontWeight: '700',
                        color: summary.etl.failure > 0 ? '#ef4444' : currentTheme === 'dark' ? '#9ca3af' : '#6b7280',
                        lineHeight: '1'
                      }}>
                        {summary.etl.failure}
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: currentTheme === 'dark' ? '#9ca3af' : '#6b7280',
                        fontWeight: '500'
                      }}>
                        Failed
                      </div>
                    </div>
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: currentTheme === 'dark' ? '#9ca3af' : '#6b7280',
                    fontWeight: '500'
                  }}>
                    {summary.etl.total > 0 ? `${summary.etl.total} total` : 'No runs'}
                  </div>
                </div>
              </div>
              
              {/* API Section */}
              <div style={{
                padding: '16px',
                backgroundColor: currentTheme === 'dark' ? '#1f2937' : '#f9fafb',
                borderRadius: '12px',
                border: `1px solid ${summary.api.failure > 0 ? 'rgba(239, 68, 68, 0.3)' : currentTheme === 'dark' ? '#374151' : '#e5e7eb'}`,
                boxShadow: summary.api.failure > 0 ? '0 0 0 1px rgba(239, 68, 68, 0.1)' : 'none'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <i className="pi pi-server" style={{ 
                          fontSize: '16px', 
                          color: currentTheme === 'dark' ? '#9ca3af' : '#6b7280' 
                        }}></i>
                        <span style={{
                          fontSize: '14px', 
                          fontWeight: '600', 
                          color: currentTheme === 'dark' ? '#f9fafb' : '#111827'
                        }}>
                          APIs
                          {summary.api.failure > 0 && (
                            <span style={{
                              marginLeft: '6px',
                              fontSize: '11px',
                              color: '#ef4444',
                              fontWeight: '600'
                            }}>
                              ({summary.api.failure} failed)
                            </span>
                          )}
                        </span>
                      </div>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    backgroundColor: getStatusColor(summary.api.success, summary.api.failure, summary.api.total),
                    border: `2px solid ${currentTheme === 'dark' ? '#374151' : '#ffffff'}`,
                    boxShadow: '0 0 0 2px rgba(0,0,0,0.1)'
                  }}></div>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{display: 'flex', gap: '16px'}}>
                    <div style={{textAlign: 'center'}}>
                      <div style={{
                        fontSize: '20px',
                        fontWeight: '700',
                        color: '#10b981',
                        lineHeight: '1'
                      }}>
                        {summary.api.success}
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: currentTheme === 'dark' ? '#9ca3af' : '#6b7280',
                        fontWeight: '500'
                      }}>
                        Success
                      </div>
                    </div>
                    <div style={{textAlign: 'center'}}>
                      <div style={{
                        fontSize: '20px',
                        fontWeight: '700',
                        color: summary.api.failure > 0 ? '#ef4444' : currentTheme === 'dark' ? '#9ca3af' : '#6b7280',
                        lineHeight: '1'
                      }}>
                        {summary.api.failure}
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: currentTheme === 'dark' ? '#9ca3af' : '#6b7280',
                        fontWeight: '500'
                      }}>
                        Failed
                      </div>
                    </div>
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: currentTheme === 'dark' ? '#9ca3af' : '#6b7280',
                    fontWeight: '500'
                  }}>
                    {summary.api.total > 0 ? `${summary.api.total} total` : 'No runs'}
                  </div>
                </div>
                                </div>
                </div>
              )}
              
              {/* Failed Details Section */}
              {failedJobs.length > 0 && (
                <div style={{
                  marginTop: '16px',
                  padding: '16px',
                  backgroundColor: currentTheme === 'dark' ? '#1f2937' : '#f9fafb',
                  borderRadius: '12px',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  boxShadow: '0 0 0 1px rgba(239, 68, 68, 0.1)'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px'
                  }}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                      <i className="pi pi-exclamation-triangle" style={{ 
                        fontSize: '16px', 
                        color: '#ef4444' 
                      }}></i>
                      <span style={{
                        fontSize: '14px', 
                        fontWeight: '600', 
                        color: '#ef4444'
                      }}>
                        Failed Jobs ({failedJobs.length})
                      </span>
                    </div>
                  </div>
                  
                  <div style={{
                    maxHeight: '200px',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    {failedJobs.slice(0, 5).map((job, index) => (
                      <div key={index} style={{
                        padding: '8px',
                        backgroundColor: currentTheme === 'dark' ? '#374151' : '#ffffff',
                        borderRadius: '6px',
                        border: '1px solid rgba(239, 68, 68, 0.2)'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '4px'
                        }}>
                          <span style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: currentTheme === 'dark' ? '#f9fafb' : '#111827'
                          }}>
                            {job.jobName}
                          </span>
                          <span style={{
                            fontSize: '10px',
                            color: currentTheme === 'dark' ? '#9ca3af' : '#6b7280',
                            fontWeight: '500'
                          }}>
                            {new Date(job.date).toLocaleDateString()}
                          </span>
                        </div>
                        <div style={{
                          fontSize: '10px',
                          color: currentTheme === 'dark' ? '#9ca3af' : '#6b7280',
                          marginBottom: '2px'
                        }}>
                          {job.category}
                        </div>
                        {job.errorCode && (
                          <div style={{
                            fontSize: '10px',
                            color: '#ef4444',
                            fontWeight: '500'
                          }}>
                            Error: {job.errorCode}
                          </div>
                        )}
                        {job.errorMessage && (
                          <div style={{
                            fontSize: '10px',
                            color: '#ef4444',
                            fontStyle: 'italic',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {job.errorMessage}
                          </div>
                        )}
                      </div>
                    ))}
                    {failedJobs.length > 5 && (
                      <div style={{
                        textAlign: 'center',
                        fontSize: '11px',
                        color: currentTheme === 'dark' ? '#9ca3af' : '#6b7280',
                        fontStyle: 'italic',
                        padding: '4px'
                      }}>
                        +{failedJobs.length - 5} more failures
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </button>
      
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}
      </style>
    </div>
  )
} 